import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { parseEvolutionError } from '@/lib/evolutionError';
import { useAuth } from '@/hooks/useAuth';
import { campaignStatuses, getStatusConfig, type Campaign } from '@/mocks/campaignsData';
import type { Group, Template } from '@/types';
import CampaignModal from './components/CampaignModal';
import ActionMenu from '@/components/base/ActionMenu';

export default function CampaignsPage() {
  const { profile } = useAuth();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todas');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  // 💾 Restore modal open state from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('campaigns_modal_state');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.modalOpen) {
          setModalOpen(true);
          if (parsed.editingCampaign) {
            setEditingCampaign(parsed.editingCampaign as Campaign);
          }
        }
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  // 💾 Save modal open state to sessionStorage whenever it changes
  useEffect(() => {
    if (modalOpen) {
      sessionStorage.setItem('campaigns_modal_state', JSON.stringify({
        modalOpen: true,
        editingCampaign: editingCampaign ? { id: editingCampaign.id } : null,
      }));
    } else {
      sessionStorage.removeItem('campaigns_modal_state');
    }
  }, [modalOpen, editingCampaign]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activating, setActivating] = useState<string | null>(null);
  const [processingQueue, setProcessingQueue] = useState<string | null>(null);
  const [processingBrowser, setProcessingBrowser] = useState(false);
  const [browserProgress, setBrowserProgress] = useState({ current: 0, total: 0 });
  const [queueMessage, setQueueMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const companyId = profile?.company_id;

  // ─── Fetch data ───
  const fetchCampaigns = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('campaigns')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setCampaigns((data || []) as Campaign[]);
    }
    setLoading(false);
  }, [companyId]);

  const fetchGroups = useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('groups')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });
    if (data) setGroups(data as Group[]);
  }, [companyId]);

  const fetchTemplates = useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('templates')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
    if (data) setTemplates(data as Template[]);
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchCampaigns();
      fetchGroups();
      fetchTemplates();
    }
  }, [companyId, fetchCampaigns, fetchGroups, fetchTemplates]);

  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      const matchSearch = c.name.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'Todas' || c.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [campaigns, search, statusFilter]);

  const stats = useMemo(() => {
    const total = campaigns.length;
    const active = campaigns.filter((c) => c.status === 'ativa').length;
    const scheduled = campaigns.filter((c) => c.status === 'agendada').length;
    const draft = campaigns.filter((c) => c.status === 'rascunho').length;
    const completed = campaigns.filter((c) => c.status === 'finalizada').length;
    const totalSent = campaigns.reduce((sum, c) => sum + c.sent_count, 0);
    const totalDelivered = campaigns.reduce((sum, c) => sum + c.delivered_count, 0);
    return { total, active, scheduled, draft, completed, totalSent, totalDelivered };
  }, [campaigns]);

  const handleSave = async (data: Omit<Campaign, 'id' | 'created_at' | 'updated_at' | 'sent_count' | 'delivered_count' | 'read_count' | 'replied_count' | 'failed_count' | 'started_at' | 'completed_at'>) => {
    if (!companyId) {
      setSaveError('Empresa não identificada. Tente recarregar a página.');
      return;
    }
    setSaving(true);
    setSaveError(null);

    const now = new Date().toISOString();

    try {
      if (editingCampaign) {
        const updates: Record<string, unknown> = {
          name: data.name,
          message_text: data.message_text,
          link_url: data.link_url,
          status: data.status,
          total_contacts: data.total_contacts,
          target_groups: data.target_groups,
          target_type: data.target_type,
          scheduled_at: data.scheduled_at,
          media_type: data.media_type,
          media_url: data.media_url,
          message_variants: (data as any).message_variants || null,
          daily_limit: (data as any).daily_limit || null,
          updated_at: now,
        };
        if (data.status === 'ativa' && !editingCampaign.started_at) {
          updates.started_at = now;
        }
        if (data.status === 'finalizada' || data.status === 'cancelada') {
          updates.completed_at = now;
        }

        const { error: updError } = await supabase
          .from('campaigns')
          .update(updates)
          .eq('id', editingCampaign.id);

        if (updError) {
          setSaveError(updError.message);
          return;
        }

        setCampaigns((prev) =>
          prev.map((c) => (c.id === editingCampaign.id ? { ...c, ...updates } as Campaign : c)),
        );
      } else {
        const newCampaign = {
          company_id: companyId,
          name: data.name,
          message_text: data.message_text,
          link_url: data.link_url,
          status: data.status,
          total_contacts: data.total_contacts,
          target_groups: data.target_groups,
          target_type: data.target_type,
          scheduled_at: data.scheduled_at,
          media_type: data.media_type,
          media_url: data.media_url,
          message_variants: (data as any).message_variants || null,
          daily_limit: (data as any).daily_limit || null,
          sent_count: 0,
          delivered_count: 0,
          read_count: 0,
          replied_count: 0,
          failed_count: 0,
          started_at: data.status === 'ativa' ? now : null,
          created_by: profile?.id || null,
        };

        const { data: inserted, error: insError } = await supabase
          .from('campaigns')
          .insert(newCampaign)
          .select()
          .maybeSingle();

        if (insError) {
          setSaveError(insError.message);
          return;
        }

        if (!inserted) {
          setSaveError('Nenhum dado retornado do servidor. A campanha pode não ter sido criada.');
          return;
        }

        const newCampaignData = inserted as Campaign;
        setCampaigns((prev) => [newCampaignData, ...prev]);

        // If campaign was created as 'ativa', auto-populate the queue right away
        if (data.status === 'ativa') {
          setSaving(false);
          setModalOpen(false);
          setEditingCampaign(null);

          const campaignId = newCampaignData.id;
          setActivating(campaignId);

          const { data: popData, error: popError } = await supabase.functions.invoke('populate-campaign-queue', {
            body: { campaign_id: campaignId },
          });
          setActivating(null);

          if (!popError && popData?.success && popData.created > 0) {
            if (popData.totalContacts !== undefined) {
              setCampaigns((prev) =>
                prev.map((c) => (c.id === campaignId ? { ...c, total_contacts: popData.totalContacts } as Campaign : c)),
              );
            }
            // Try auto-process via Edge Function
            const { data: procData, error: procError } = await supabase.functions.invoke('process-queue', {
              body: {},
            });

            if (procError) {
              setQueueMessage({
                type: 'error',
                text: `Fila criada (${popData.created} jobs). Edge Function indisponível, enviando pelo navegador...`,
              });
              setTimeout(() => processQueueFromBrowser(), 500);
            } else if (procData?.success) {
              let totalProcessed3 = procData.processed || 0;
              let totalFailed3 = procData.failed || 0;
              let remaining3 = procData.remaining || 0;
              let iter3 = 0;
              const maxIter3 = 30;

              while (remaining3 > 0 && iter3 < maxIter3) {
                iter3++;
                setQueueMessage({
                  type: 'success',
                  text: `Processando... ${totalProcessed3} enviadas, ${totalFailed3} falhas (${remaining3} restantes)`,
                });
                await new Promise((r) => setTimeout(r, 1500));

                const { data: nextData3, error: nextError3 } = await supabase.functions.invoke('process-queue', {
                  body: {},
                });

                if (nextError3 || !nextData3?.success) break;
                totalProcessed3 += (nextData3.processed || 0);
                totalFailed3 += (nextData3.failed || 0);
                remaining3 = nextData3.remaining || 0;
              }

              setQueueMessage({
                type: 'success',
                text: `Fila processada! ${totalProcessed3} enviadas, ${totalFailed3} falhas.`,
              });
              fetchCampaigns();
            } else {
              setQueueMessage({
                type: 'error',
                text: procData?.message || `Fila criada mas houve erro ao processar. Clique em "Processar".`,
              });
            }
          } else if (popError || !popData?.success) {
            setQueueMessage({
              type: 'error',
              text: popData?.message || popError?.message || 'Campanha criada mas não foi possível popular a fila automática.',
            });
          } else {
            setQueueMessage({
              type: 'error',
              text: popData?.message || 'Nenhum contato encontrado nos grupos. Verifique se os contatos estão nos grupos corretos.',
            });
          }
          setTimeout(() => setQueueMessage(null), 10000);
          return;
        }
      }

      // Sucesso — fecha o modal (for non-active campaigns or edits)
      setSaveError(null);
      setSaving(false);
      setModalOpen(false);
      setEditingCampaign(null);
      sessionStorage.removeItem('campaigns_modal_state');
      sessionStorage.removeItem('campaign_draft');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Erro inesperado ao salvar campanha');
      setSaving(false);
    }
  };

  const updateStatus = async (id: string, newStatus: Campaign['status']) => {
    const now = new Date().toISOString();
    const updates: Record<string, unknown> = {
      status: newStatus,
      updated_at: now,
    };
    if (newStatus === 'ativa') updates.started_at = now;
    if (newStatus === 'finalizada' || newStatus === 'cancelada') updates.completed_at = now;
    if (newStatus === 'rascunho') {
      updates.started_at = null;
      updates.completed_at = null;
    }

    const { error: updError } = await supabase
      .from('campaigns')
      .update(updates)
      .eq('id', id);

    if (!updError) {
      setCampaigns((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...updates } as Campaign : c)),
      );

      // When activating a campaign, populate the queue AND auto-process
      if (newStatus === 'ativa') {
        setActivating(id);
        setQueueMessage(null);

        const { data: popData, error: popError } = await supabase.functions.invoke('populate-campaign-queue', {
          body: { campaign_id: id },
        });

        if (popError || !popData?.success) {
          setActivating(null);
          setQueueMessage({
            type: 'error',
            text: popData?.message || popError?.message || 'Erro ao popular fila',
          });
        } else {
          // Update campaign list with new total_contacts
          if (popData.totalContacts !== undefined) {
            setCampaigns((prev) =>
              prev.map((c) => (c.id === id ? { ...c, total_contacts: popData.totalContacts } as Campaign : c)),
            );
          }

          // Auto-process queue immediately if jobs were created
          if (popData.created > 0) {
            // First try Edge Function
            const { data: procData, error: procError } = await supabase.functions.invoke('process-queue', {
              body: {},
            });

            setActivating(null);

            if (procError) {
              // Edge function failed - fallback to browser-side processing
              setQueueMessage({
                type: 'error',
                text: `Fila criada (${popData.created} jobs). Edge Function indisponível, enviando pelo navegador...`,
              });
              setTimeout(() => processQueueFromBrowser(), 500);
            } else if (procData?.success) {
              // Auto-continue processing remaining jobs
              let totalProcessed = procData.processed || 0;
              let totalFailed = procData.failed || 0;
              let remaining = procData.remaining || 0;
              let iter = 0;
              const maxIter = 30;

              while (remaining > 0 && iter < maxIter) {
                iter++;
                setQueueMessage({
                  type: 'success',
                  text: `Processando... ${totalProcessed} enviadas, ${totalFailed} falhas (${remaining} restantes)`,
                });
                await new Promise((r) => setTimeout(r, 1500));

                const { data: nextData, error: nextError } = await supabase.functions.invoke('process-queue', {
                  body: {},
                });

                if (nextError || !nextData?.success) break;
                totalProcessed += (nextData.processed || 0);
                totalFailed += (nextData.failed || 0);
                remaining = nextData.remaining || 0;
              }

              setQueueMessage({
                type: 'success',
                text: `Fila processada! ${totalProcessed} enviadas, ${totalFailed} falhas.`,
              });
              fetchCampaigns();
            } else {
              // Edge function returned but reported failure
              setQueueMessage({
                type: 'error',
                text: procData?.message || `Fila criada, mas houve erro ao processar. Tente clicar em "Processar".`,
              });
            }
          } else {
            setActivating(null);
            setQueueMessage({
              type: 'success',
              text: popData.message || `${popData.created || 0} jobs criados na fila!`,
            });
          }
        }
        setTimeout(() => setQueueMessage(null), 8000);
      }
    }
  };

  const handleProcessQueue = async (campaignId: string) => {
    setProcessingQueue(campaignId);
    setQueueMessage(null);

    let totalProcessed = 0;
    let totalFailed = 0;
    let keepGoing = true;
    const maxIterations = 30; // safety limit
    let iteration = 0;

    while (keepGoing && iteration < maxIterations) {
      iteration++;
      const { data, error } = await supabase.functions.invoke('process-queue', {
        body: {},
      });

      if (error) {
        setProcessingQueue(null);
        setQueueMessage({
          type: 'error',
          text: `Edge Function indisponível. Tentando envio direto pelo navegador...`,
        });
        setTimeout(() => processQueueFromBrowser(), 500);
        return;
      }

      if (!data?.success) {
        setProcessingQueue(null);
        setQueueMessage({
          type: 'error',
          text: data?.message || 'Erro ao processar fila',
        });
        fetchCampaigns();
        setTimeout(() => setQueueMessage(null), 6000);
        return;
      }

      totalProcessed += (data.processed || 0);
      totalFailed += (data.failed || 0);
      const remaining = data.remaining || 0;

      setQueueMessage({
        type: 'success',
        text: `Processando... ${totalProcessed} enviadas, ${totalFailed} falhas${remaining > 0 ? ` (${remaining} restantes)` : ''}`,
      });

      if (remaining === 0) {
        keepGoing = false;
      } else {
        // small delay between batches to avoid overwhelming the server
        await new Promise((r) => setTimeout(r, 1500));
      }
    }

    setProcessingQueue(null);
    setQueueMessage({
      type: 'success',
      text: `Fila processada! ${totalProcessed} enviadas, ${totalFailed} falhas.`,
    });
    fetchCampaigns();
    setTimeout(() => setQueueMessage(null), 8000);
  };

  // Browser-side queue processor - sends directly from browser to Evolution API
  const processQueueFromBrowser = async () => {
    if (processingBrowser) return;
    setProcessingBrowser(true);
    setQueueMessage(null);

    try {
      const { data: waConfig } = await supabase
        .from('whatsapp_configs')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();

      if (!waConfig?.evolution_server_url || !waConfig?.evolution_api_key || !waConfig?.evolution_instance_name) {
        setQueueMessage({ type: 'error', text: 'Evolution API não configurada. Configure em Configurações > Evolution API.' });
        setProcessingBrowser(false);
        return;
      }

      const baseUrl = waConfig.evolution_server_url.replace(/\/+$/, '');
      const instanceName = waConfig.evolution_instance_name;
      const apiKey = waConfig.evolution_api_key;
      const rateLimitPerMinute = waConfig.rate_limit_per_minute || 30;
      const baseDelayMs = Math.max(1000, Math.floor(60000 / rateLimitPerMinute));

      // 🛡️ Anti-spam configs
      const jitterMaxMs = waConfig.anti_spam_jitter_ms ?? 5000;
      const globalDailyLimit = waConfig.anti_spam_daily_limit ?? 200;
      const warmupCount = waConfig.anti_spam_warmup_count ?? 50;
      const warmupDelayMs = waConfig.anti_spam_warmup_delay_ms ?? 12000;

      // 🎲 Spintax processor
      const processSpintax = (text: string): string => {
        return text.replace(/\{([^}]+)\}/g, (_match: string, options: string) => {
          const parts = options.split('|');
          return parts[Math.floor(Math.random() * parts.length)];
        });
      };

      // 🎲 Jitter helper
      const getJitteredDelay = (base: number, jitterMax: number): number => {
        return base + Math.floor(Math.random() * jitterMax);
      };

      // 🎲 Warm-up helper
      const getWarmupDelay = (idx: number, normalDelay: number) => {
        if (idx < warmupCount) return getJitteredDelay(warmupDelayMs, jitterMaxMs);
        return getJitteredDelay(normalDelay, jitterMaxMs);
      };

      const cleanUrl = (url: string): string => {
        try {
          const u = new URL(url);
          if (u.hostname === 'youtu.be') {
            const videoId = u.pathname.replace('/', '');
            if (videoId) {
              u.hostname = 'www.youtube.com';
              u.pathname = '/watch';
              u.search = `?v=${videoId}`;
            }
          }
          if (u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')) {
            u.searchParams.delete('si');
            u.searchParams.delete('feature');
            u.searchParams.delete('pp');
            u.searchParams.delete('utm_source');
            u.searchParams.delete('utm_medium');
            u.searchParams.delete('utm_campaign');
          }
          return u.toString();
        } catch {
          return url;
        }
      };

      const cleanMessageUrls = (msg: string): string => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return msg.replace(urlRegex, (url) => cleanUrl(url));
      };

      // 🎲 Build message with spintax + variants
      const buildMsg = (text: string, link: string | null, variants: string[] | null): string => {
        let chosenText = text;
        if (variants && variants.length > 0) {
          const allVariants = [text, ...variants].filter(v => v && v.trim().length > 0);
          chosenText = allVariants[Math.floor(Math.random() * allVariants.length)] || text;
        }
        chosenText = processSpintax(chosenText);
        const cleaned = cleanMessageUrls(chosenText);
        if (!link) return cleaned;
        return `${cleaned}\n\n${cleanUrl(link)}`;
      };

      // Try instance state
      try {
        const stateUrl = `${baseUrl}/instance/connectionState/${instanceName}`;
        const stateRes = await fetch(stateUrl, {
          headers: { 'Content-Type': 'application/json', apikey: apiKey },
        });
        if (stateRes.ok) {
          const stateData = await stateRes.json();
          if (stateData?.instance?.state !== 'open') {
            setQueueMessage({
              type: 'error',
              text: `Instância "${instanceName}" está "${stateData?.instance?.state || 'desconhecida'}". Vá em Configurações > Evolution API e escaneie o QR Code para reconectar.`,
            });
            setProcessingBrowser(false);
            return;
          }
        }
      } catch {
        setQueueMessage({ type: 'error', text: 'Não foi possível verificar o estado da instância Evolution API.' });
        setProcessingBrowser(false);
        return;
      }

      // Get queued jobs
      const { data: jobs, error: jobsError } = await supabase
        .from('queue_jobs')
        .select('*')
        .eq('status', 'queued')
        .order('created_at', { ascending: true })
        .limit(50);

      if (jobsError) {
        setQueueMessage({ type: 'error', text: `Erro ao buscar fila: ${jobsError.message}` });
        setProcessingBrowser(false);
        return;
      }

      if (!jobs || jobs.length === 0) {
        setQueueMessage({ type: 'success', text: 'Fila vazia — nenhum job pendente.' });
        setProcessingBrowser(false);
        return;
      }

      setBrowserProgress({ current: 0, total: jobs.length });

      let sent = 0;
      let failed = 0;
      let dailyLimited = 0;

      // 🛡️ Track daily counts & campaign configs
      const dailySentMap = new Map<string, number>();
      const campaignConfigMap = new Map<string, { dailyLimit: number; messageVariants: string[] | null }>();

      for (let i = 0; i < jobs.length; i++) {
        const job = jobs[i];
        setBrowserProgress({ current: i + 1, total: jobs.length });

        const campaignId = job.campaign_id;

        // ─── Fetch campaign config (cache) ───
        if (!campaignConfigMap.has(campaignId)) {
          const { data: campaignCfg } = await supabase
            .from('campaigns')
            .select('daily_limit, message_variants')
            .eq('id', campaignId)
            .maybeSingle();

          const campDailyLimit = campaignCfg?.daily_limit || globalDailyLimit;
          const campVariants = campaignCfg?.message_variants || null;

          campaignConfigMap.set(campaignId, {
            dailyLimit: campDailyLimit,
            messageVariants: campVariants,
          });

          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          const { count: sentToday } = await supabase
            .from('queue_jobs')
            .select('*', { count: 'exact', head: true })
            .eq('campaign_id', campaignId)
            .eq('status', 'completed')
            .gte('processed_at', todayStart.toISOString());

          dailySentMap.set(campaignId, sentToday || 0);
        }

        const campCfg = campaignConfigMap.get(campaignId)!;
        const sentToday = dailySentMap.get(campaignId) || 0;

        // 🛡️ Daily limit check
        if (sentToday >= campCfg.dailyLimit) {
          dailyLimited++;
          continue;
        }

        const { data: campaign } = await supabase
          .from('campaigns')
          .select('*')
          .eq('id', campaignId)
          .maybeSingle();

        const { data: contact } = await supabase
          .from('contacts')
          .select('*')
          .eq('id', job.contact_id)
          .maybeSingle();

        const rawPhone = contact?.phone || '';
        const normalizePhone = (p: string): string => {
          const digits = p.replace(/\D/g, '');
          if (digits.startsWith('55')) return digits;
          return '55' + digits;
        };
        const phone = normalizePhone(rawPhone);

        // 🎲 Build message with spintax + variants
        const message = buildMsg(campaign?.message_text || '', campaign?.link_url || null, campCfg.messageVariants);

        if (!rawPhone || !message) {
          await supabase.from('queue_jobs').update({
            status: 'failed',
            last_error: 'Dados inválidos',
            processed_at: new Date().toISOString(),
          }).eq('id', job.id);
          failed++;
          continue;
        }

        const hasMedia = campaign?.media_type && campaign?.media_url &&
          ['image', 'video', 'audio', 'document'].includes(campaign.media_type);

        let sendUrl: string;
        let payload: Record<string, unknown>;

        if (hasMedia) {
          sendUrl = `${baseUrl}/message/sendMedia/${instanceName}`;
          payload = {
            number: phone,
            mediatype: campaign.media_type,
            media: campaign.media_url,
            caption: message,
          };
        } else {
          sendUrl = `${baseUrl}/message/sendText/${instanceName}`;
          payload = { number: phone, text: message, linkPreview: true, delay: 1500 };
        }

        try {
          const evoRes = await fetch(sendUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: apiKey,
            },
            body: JSON.stringify(payload),
          });

          const evoData = await evoRes.json();

          if (!evoRes.ok) {
            const errorMsg = parseEvolutionError(evoData, phone);
            const newAttempt = (job.attempt || 0) + 1;
            const isFinal = newAttempt >= (job.max_attempts || 3);
            const isNumberNotOnWhatsApp = errorMsg.includes('nao tem WhatsApp');
            const finalStatus = isNumberNotOnWhatsApp ? 'failed' : (isFinal ? 'failed' : 'queued');

            await supabase.from('queue_jobs').update({
              attempt: newAttempt,
              last_error: errorMsg,
              status: finalStatus,
              processed_at: new Date().toISOString(),
            }).eq('id', job.id);

            if (campaignId) {
              await supabase.rpc('increment_campaign_failed', { campaign_id: campaignId });
            }
            failed++;
          } else {
            const msgId = evoData?.key?.id || evoData?.messageId || null;

            await supabase.from('queue_jobs').update({
              status: 'completed',
              processed_at: new Date().toISOString(),
            }).eq('id', job.id);

            // Track daily count
            dailySentMap.set(campaignId, sentToday + 1);

            const { data: existingCC } = await supabase
              .from('campaign_contacts')
              .select('id')
              .eq('campaign_id', campaignId)
              .eq('contact_id', job.contact_id)
              .maybeSingle();

            if (existingCC) {
              await supabase.from('campaign_contacts').update({
                status: 'sent',
                wa_message_id: msgId,
                sent_at: new Date().toISOString(),
              }).eq('id', existingCC.id);
            } else {
              await supabase.from('campaign_contacts').insert({
                campaign_id: campaignId,
                contact_id: job.contact_id,
                status: 'sent',
                wa_message_id: msgId,
                sent_at: new Date().toISOString(),
              });
            }

            if (campaignId) {
              await supabase.rpc('increment_campaign_sent', { campaign_id: campaignId });
            }
            sent++;
          }
        } catch {
          const newAttempt = (job.attempt || 0) + 1;
          const isFinal = newAttempt >= (job.max_attempts || 3);
          await supabase.from('queue_jobs').update({
            attempt: newAttempt,
            last_error: 'Erro de rede',
            status: isFinal ? 'failed' : 'queued',
            processed_at: new Date().toISOString(),
          }).eq('id', job.id);
          failed++;
        }

        // 🛡️ Anti-spam delay with Jitter + Warm-up
        if (i < jobs.length - 1) {
          const delayMs = getWarmupDelay(sent, baseDelayMs);
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }

      const uniqueCampaignIds = [...new Set(jobs.map((j: { campaign_id: string }) => j.campaign_id))];
      for (const cid of uniqueCampaignIds) {
        const { data: pending } = await supabase
          .from('queue_jobs')
          .select('id')
          .eq('campaign_id', cid)
          .in('status', ['queued', 'processing'])
          .limit(1);
        if (pending && pending.length === 0) {
          await supabase
            .from('campaigns')
            .update({ status: 'finalizada', completed_at: new Date().toISOString(), updated_at: new Date().toISOString() })
            .eq('id', cid)
            .eq('status', 'ativa');
        }
      }

      const { count: remaining } = await supabase
        .from('queue_jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'queued');

      const remainingCount = remaining || 0;

      setQueueMessage({
        type: sent > 0 ? 'success' : 'error',
        text: `${sent} enviadas, ${failed} falhas de ${jobs.length} mensagens${dailyLimited > 0 ? ` (${dailyLimited} pausadas pelo limite diario)` : ''}${remainingCount > 0 ? ` (${remainingCount} restantes — continuando...)` : ''}`,
      });

      await fetchCampaigns();

      if (remainingCount > 0) {
        setTimeout(() => processQueueFromBrowser(), 1000);
        return;
      }
    } catch (err) {
      setQueueMessage({
        type: 'error',
        text: `Erro: ${err instanceof Error ? err.message : 'Erro inesperado'}`,
      });
    }

    setProcessingBrowser(false);
    setBrowserProgress({ current: 0, total: 0 });
    setTimeout(() => setQueueMessage(null), 8000);
  };

  const deleteCampaign = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta campanha?')) return;
    const { error: delError } = await supabase
      .from('campaigns')
      .delete()
      .eq('id', id);

    if (!delError) {
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
    }
  };

  const openCreate = () => {
    setEditingCampaign(null);
    setModalOpen(true);
  };

  const openEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setModalOpen(true);
  };

  const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const progressPercent = (sent: number, total: number) => {
    return total > 0 ? Math.round((sent / total) * 100) : 0;
  };

  // Build action items for a campaign based on its status
  const getCampaignActions = (campaign: Campaign) => {
    const actions = [];

    if (campaign.status === 'rascunho') {
      actions.push({
        label: 'Agendar',
        icon: 'ri-time-line',
        iconColor: 'text-accent-500',
        onClick: () => updateStatus(campaign.id, 'agendada'),
      });
      actions.push({
        label: 'Iniciar agora',
        icon: 'ri-play-line',
        iconColor: 'text-primary-500',
        onClick: () => updateStatus(campaign.id, 'ativa'),
      });
    }

    if (campaign.status === 'ativa') {
      actions.push({
        label: 'Pausar',
        icon: 'ri-pause-line',
        iconColor: 'text-amber-500',
        onClick: () => updateStatus(campaign.id, 'pausada'),
      });
      actions.push({
        label: 'Processar fila',
        icon: 'ri-send-plane-fill',
        iconColor: 'text-accent-500',
        onClick: () => handleProcessQueue(campaign.id),
        disabled: processingQueue === campaign.id,
      });
    }

    if (campaign.status === 'pausada') {
      actions.push({
        label: 'Retomar',
        icon: 'ri-play-line',
        iconColor: 'text-primary-500',
        onClick: () => updateStatus(campaign.id, 'ativa'),
      });
      actions.push({
        label: 'Processar fila',
        icon: 'ri-send-plane-fill',
        iconColor: 'text-accent-500',
        onClick: () => handleProcessQueue(campaign.id),
        disabled: processingQueue === campaign.id,
      });
    }

    if (campaign.status === 'agendada') {
      actions.push({
        label: 'Ativar agora',
        icon: 'ri-play-line',
        iconColor: 'text-primary-500',
        onClick: () => updateStatus(campaign.id, 'ativa'),
      });
    }

    if (campaign.status === 'ativa' || campaign.status === 'pausada' || campaign.status === 'agendada') {
      actions.push({
        label: 'Cancelar',
        icon: 'ri-close-line',
        iconColor: 'text-red-500',
        onClick: () => updateStatus(campaign.id, 'cancelada'),
      });
    }

    if (campaign.status === 'ativa' || campaign.status === 'pausada') {
      actions.push({
        label: 'Ver progresso',
        icon: 'ri-bar-chart-line',
        iconColor: 'text-emerald-500',
        onClick: () => window.REACT_APP_NAVIGATE(`/campaigns/${campaign.id}/progress`),
      });
    }

    actions.push({
      label: 'Editar',
      icon: 'ri-edit-line',
      iconColor: 'text-primary-500',
      onClick: () => openEdit(campaign),
    });

    actions.push({
      label: 'Excluir',
      icon: 'ri-delete-bin-line',
      danger: true,
      onClick: () => deleteCampaign(campaign.id),
    });

    return actions;
  };

  // ─── Loading / Error states ───
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="h-8 w-36 bg-background-200 rounded animate-pulse" />
            <div className="h-4 w-56 bg-background-100 rounded mt-1 animate-pulse" />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-12 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-foreground-500">Carregando campanhas...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-heading font-bold text-foreground-950">Campanhas</h1>
        <div className="bg-white rounded-xl border border-background-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <p className="text-sm text-foreground-700 font-medium mb-1">Erro ao carregar campanhas</p>
          <p className="text-xs text-foreground-500 mb-4">{error}</p>
          <button
            onClick={fetchCampaigns}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950">Campanhas</h1>
          <p className="text-sm text-foreground-500 mt-0.5">Gerencie envios em massa e acompanhe resultados</p>
        </div>
        <button
          onClick={openCreate}
          disabled={saving}
          className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          <i className="ri-add-line text-lg" />
          Nova campanha
        </button>
      </div>

      {/* Browser processing progress */}
      {processingBrowser && (
        <div className="rounded-xl px-4 py-3 bg-sky-50 border border-sky-200">
          <div className="flex items-center gap-3 mb-2">
            <i className="ri-loader-4-line animate-spin text-sky-600 text-lg" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-sky-700">Enviando mensagens pelo navegador...</p>
              <p className="text-xs text-sky-500">{browserProgress.current} de {browserProgress.total} mensagens</p>
            </div>
          </div>
          <div className="w-full h-2 bg-sky-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-sky-500 rounded-full transition-all duration-300"
              style={{ width: `${browserProgress.total > 0 ? Math.round((browserProgress.current / browserProgress.total) * 100) : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Queue message banner */}
      {queueMessage && (
        <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${
          queueMessage.type === 'success'
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <i className={`text-lg ${
            queueMessage.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-error-warning-line text-red-500'
          }`} />
          <p className="text-sm flex-1">{queueMessage.text}</p>
          <button onClick={() => setQueueMessage(null)} className="text-foreground-400 hover:text-foreground-600 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white rounded-lg border border-background-200 p-3">
          <p className="text-xs text-foreground-500">Total</p>
          <p className="text-xl font-bold text-foreground-950">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-3">
          <p className="text-xs text-foreground-500">Ativas</p>
          <p className="text-xl font-bold text-primary-600">{stats.active}</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-3">
          <p className="text-xs text-foreground-500">Agendadas</p>
          <p className="text-xl font-bold text-accent-600">{stats.scheduled}</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-3">
          <p className="text-xs text-foreground-500">Rascunhos</p>
          <p className="text-xl font-bold text-secondary-600">{stats.draft}</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-3">
          <p className="text-xs text-foreground-500">Concluídas</p>
          <p className="text-xl font-bold text-emerald-600">{stats.completed}</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-3">
          <p className="text-xs text-foreground-500">Enviadas</p>
          <p className="text-xl font-bold text-foreground-800">{stats.totalSent.toLocaleString('pt-BR')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="Buscar campanha..."
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => setStatusFilter('Todas')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border ${
              statusFilter === 'Todas'
                ? 'bg-foreground-950 text-white border-foreground-950'
                : 'bg-white text-foreground-600 border-background-200 hover:bg-background-50'
            }`}
          >
            Todas
          </button>
          {campaignStatuses.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                statusFilter === s.value
                  ? 'bg-foreground-950 text-white border-foreground-950'
                  : 'bg-white text-foreground-600 border-background-200 hover:bg-background-50'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Campaigns list */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-background-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-megaphone-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm text-foreground-500">Nenhuma campanha encontrada</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((campaign) => {
            const statusConfig = getStatusConfig(campaign.status);
            const progress = progressPercent(campaign.sent_count, campaign.total_contacts);
            const actions = getCampaignActions(campaign);

            return (
              <div
                key={campaign.id}
                className="bg-white rounded-xl border border-background-200 p-5 hover:border-primary-300 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {/* Left: Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium border ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                      {campaign.scheduled_at && campaign.status !== 'ativa' && campaign.status !== 'finalizada' && campaign.status !== 'cancelada' && (
                        <span className="px-2 py-1 bg-accent-50 text-accent-700 rounded-md text-xs font-medium border border-accent-200">
                          <i className="ri-time-line mr-1" />
                          {formatDate(campaign.scheduled_at)}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-foreground-950 text-sm mb-1 flex items-center gap-1.5">
                      {campaign.media_type === 'image' && <i className="ri-image-line text-foreground-400" />}
                      {campaign.media_type === 'video' && <i className="ri-video-line text-foreground-400" />}
                      {campaign.media_type === 'audio' && <i className="ri-volume-up-line text-foreground-400" />}
                      {campaign.media_type === 'document' && <i className="ri-file-text-line text-foreground-400" />}
                      {campaign.name}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{campaign.message_text}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground-500">
                      <span><i className="ri-group-line mr-1" />{(campaign.target_groups || []).join(', ') || 'Todos'}</span>
                      <span><i className="ri-calendar-line mr-1" />{formatDate(campaign.created_at)}</span>
                    </div>
                  </div>

                  {/* Middle: Stats */}
                  <div className="flex items-center gap-4 lg:w-48 flex-shrink-0">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-foreground-500">Progresso</span>
                        <span className="text-xs font-semibold text-foreground-700">{progress}%</span>
                      </div>
                      <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-[10px] text-foreground-400">
                        <span>{campaign.sent_count.toLocaleString('pt-BR')}</span>
                        <span>{campaign.total_contacts.toLocaleString('pt-BR')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Metrics & Actions */}
                  <div className="lg:w-56 flex-shrink-0">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="text-center p-2 bg-emerald-50 rounded-lg">
                        <p className="text-xs text-emerald-600 font-semibold">{campaign.delivered_count.toLocaleString('pt-BR')}</p>
                        <p className="text-[10px] text-emerald-500">Entregues</p>
                      </div>
                      <div className="text-center p-2 bg-primary-50 rounded-lg">
                        <p className="text-xs text-primary-600 font-semibold">{campaign.read_count.toLocaleString('pt-BR')}</p>
                        <p className="text-[10px] text-primary-500">Lidas</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <ActionMenu actions={actions} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CampaignModal
        campaign={editingCampaign}
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingCampaign(null); setSaveError(null); }}
        onSave={handleSave}
        groups={groups}
        templates={templates}
        saving={saving}
        saveError={saveError}
      />
    </div>
  );
}